import { ref, onBeforeUnmount } from 'vue'
import { logError } from '~/utils/log-error'

export function useIframeResize() {
  const removeListeners = ref<(() => void)[]>([])

  const resizeIframe = (iframeElement: HTMLIFrameElement) => {
    if (!iframeElement) return () => {}

    const resizeListener = (event: MessageEvent) => {
      if (
        !event ||
        (event.origin !== 'https://public.flourish.studio' &&
          event.origin !== 'https://flo.uri.sh')
      ) {
        return
      }

      if (event.source !== iframeElement.contentWindow) return

      try {
        const data = JSON.parse(event.data)
        if (data?.context === 'iframe.resize') {
          const height = (data?.height || 0) + 20
          iframeElement.style.height = `${height}px`
        }
      } catch (error) {
        logError(error)
      }
    }

    window.addEventListener('message', resizeListener)

    return () => window.removeEventListener('message', resizeListener)
  }

  const setupIframeResizing = (
    contentElement: HTMLElement | undefined,
    resizeIframeFunc: (iframe: HTMLIFrameElement) => () => void = resizeIframe,
  ) => {
    if (!contentElement) return

    const iframeElements = contentElement?.querySelectorAll('iframe') || []

    iframeElements.forEach((iframeElement: HTMLIFrameElement) => {
      if (!iframeElement) return
      const removeListener = resizeIframeFunc(iframeElement)
      if (typeof removeListener === 'function') {
        removeListeners.value.push(removeListener)
      }
    })
  }

  const cleanup = () => {
    if (!removeListeners.value || !removeListeners.value.length) return

    removeListeners.value.forEach((removeListener) => {
      if (typeof removeListener === 'function') {
        removeListener()
      }
    })
  }

  // For testing purposes only
  const addRemoveListener = (removeListener: () => void) => {
    removeListeners.value.push(removeListener)
  }

  onBeforeUnmount(cleanup)

  return {
    setupIframeResizing,
    resizeIframe,
    cleanup, // Expose cleanup function for testing
    addRemoveListener, // Expose addRemoveListener function for testing
  }
}
